import { isClient, isServer } from '@flyer/utils';
import { capitalize, toNumber } from 'lodash-es';
import localFont from 'next/font/local';
import { CefrLevel, FloatRange, WordDecision } from '@/__generated__/graphql';
import { ENVIRONMENT, HIDE_SCHOOL_DOMAINS } from '@/lib/config';

function getLocaleKey(keyName: string, locale?: string): string {
  return typeof locale === 'string' && locale === 'en'
    ? `${keyName}${capitalize(locale)}`
    : keyName;
}

export function getLocaleName(
  data: Record<string, unknown>,
  keyName: string,
  locale?: string,
): string {
  const name = data[getLocaleKey(keyName, locale)];

  return typeof name === 'string' ? name : '';
}

function getMediaUrl(url: undefined, type?: 'image' | 'audio' | 'avatar'): undefined;
function getMediaUrl(url: string, type?: 'image' | 'audio' | 'avatar'): string;
function getMediaUrl(url: string | undefined, type = 'image') {
  if (!url) {
    return;
  }

  if (url.startsWith('http')) {
    return url.replace('https://statics.flyer.vn', ENVIRONMENT.PUBLIC_BASE_MEDIA_URL);
  }

  return `${ENVIRONMENT.PUBLIC_BASE_MEDIA_URL}/${type}/${url}`;
}

export async function uploadAvatar(file: File | Blob, path = 'avatar') {
  const form = new FormData();
  form.append('file', file);
  if (!process.env.NEXT_PUBLIC_APP_API) {
    return null;
  }

  try {
    const response = await fetch(`/app-api/${path}`, {
      method: 'POST',
      body: form,
    });
    const json = (await response.json()) as { data: { id: string } };
    const {
      data: { id },
    } = json;

    return id;
  } catch {
    return null;
  }
}
export const configRecording = {
  type: 'audio',
  mimeType: 'audio/webm',
  numberOfAudioChannels: 1,
  desiredSampRate: 16000,
  timeSlice: 1000,
};

export async function uploadAudio(audioFile: Blob) {
  if (!process.env.NEXT_PUBLIC_APP_API) {
    return null;
  }
  const formData = new FormData();
  formData.append('file', audioFile);
  const response = await fetch(`${process.env.NEXT_PUBLIC_APP_API}/record`, {
    method: 'POST',
    body: formData,
  });
  const json = (await response.json()) as { data: { id: string; url: string } };

  return json.data;
}

export const isScrollable = (ele: Element): boolean => {
  // Compare the height to see if the element has scrollable content
  const hasScrollableContent = ele.scrollHeight > ele.clientHeight;

  return hasScrollableContent;
};

export function domainSource() {
  if (isServer()) {
    return;
  }

  const fullUrl = window.location.hostname;
  const parts = fullUrl.split('.');

  return parts?.[0];
}

export const isFlyerUs = (): boolean =>
  isClient() && window?.location?.hostname?.endsWith('flyer.us');

export const isOnlyExamFlyerUs = (): boolean =>
  isClient() && window?.location?.hostname?.includes('exam.flyer.us');

export const isOnlyIeltsFlyerVn = (): boolean =>
  isClient() && window?.location?.hostname?.includes('ielts.flyer.vn');

export const redisSet = (key: string, data: unknown, time?: number) => {
  return fetch('/exam/api/redis', {
    method: 'POST',
    body: JSON.stringify({
      key,
      data,
      time,
    }),
  });
};

export const cookieProfileId = process.env.NEXT_PUBLIC_COOKIE_PROFILE_ID as string;
export const cookieAnonymousProfileId = process.env
  .NEXT_PUBLIC_ANONYMOUS_LEARNER_PROFILE_ID as string;

export const urlMonthlyStripe = 'https://buy.stripe.com/8wM29s3Iy9SYcMg28d';
export const utmSourceId = 'utm_source';

export const utmMediumId = 'utm_medium';
export const utmCampaignId = 'utm_campaign';
export const utmContentId = 'utm_content';

// use for coupon code and referral code
export const mgmReferralCode = 'referral_code';

/**
 * Validates if a string is a valid hex color code
 * @param color - String to validate
 * @returns true if valid hex color, false otherwise
 */
export function isValidHexColor(color?: string | null): boolean {
  if (!color) return false;
  return /^#([0-9A-F]{3}){1,2}$/i.test(color);
}

/**
 * Extracts YouTube video ID from various YouTube URL formats
 * e.g. https://youtu.be/VIDEO_ID, https://www.youtube.com/watch?v=VIDEO_ID
 */
export function getYouTubeVideoId(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1) || undefined;
    }
    return parsed.searchParams.get('v') ?? undefined;
  } catch {
    return undefined;
  }
}

export default getMediaUrl;

export function isHideSchool() {
  return HIDE_SCHOOL_DOMAINS.includes(window.location.host);
}
export function fNumber(number: string | number) {
  return toNumber(number).toLocaleString('vi');
}

export const BalooBhaifont = localFont({
  src: '../../public/fonts/BalooBhai-Regular.ttf',
  variable: '--font-BalooBhai-Regular',
});

export const DinRoundProFont = localFont({
  src: [
    {
      path: '../../public/fonts/DinRoundPro/DINRoundPro-Bold.ttf',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../../public/fonts/DinRoundPro/DINRoundPro-Medi.ttf',
      weight: '500',
      style: 'normal',
    },
  ],
  variable: '--font-DINRoundPro',
});

export const LinotteFont = localFont({
  src: [
    {
      path: '../../public/fonts/Linotte/Linotte_Bold.ttf',
      weight: '700',
      style: 'normal',
    },
    {
      path: '../../public/fonts/Linotte/Linotte_Semi_Bold.ttf',
      weight: '600',
      style: 'normal',
    },
    {
      path: '../../public/fonts/Linotte/Linotte_Regular.ttf',
      weight: '500',
      style: 'normal',
    },
  ],
  variable: '--font-DINRoundPro',
});

import { Inter } from 'next/font/google';
import { atom } from 'jotai';

export const InterFont = Inter({
  subsets: ['latin'],
  variable: '--font-inter',
});

export const getColorFromScore = (decision: WordDecision | undefined) => {
  if (!decision) {
    return {};
  }
  switch (decision) {
    case WordDecision.Correct: {
      return {
        color: '#7DB42E',
        linearColor: 'linear-gradient(270deg, #7DB42E 0%, #528B00 100%)',
      };
    }
    case WordDecision.Warning: {
      return {
        color: '#FFAC2F',
        linearColor: 'linear-gradient(270deg, #FFAC2F 0%, #E58900 100%)',
      };
    }
    case WordDecision.Incorrect: {
      return {
        color: '#CC2929',
        linearColor: 'linear-gradient(270deg, #CC2929 0%, #95001E 100%)',
      };
    }
    default: {
      return {
        color: '#7DB42E',
        linearColor: 'linear-gradient(270deg, #7DB42E 0%, #528B00 100%)',
      };
    }
  }
};

export const countWord = (text: string) => {
  const words = text.trim().split(/\s+/);

  return words.filter((word) => word.length > 0).length;
};

export const listLevelData = [
  {
    title: 'Pre A1',
    value: CefrLevel.Prea1,
    color: '#9E5FDD',
  },
  {
    title: 'A1',
    value: CefrLevel.A1,
    color: '#47C199',
  },
  {
    title: 'A2',
    value: CefrLevel.A2,
    color: '#F95F3C',
  },
  {
    title: 'B1',
    value: CefrLevel.B1,
    color: '#33CF4B',
  },
  {
    title: 'B2',
    value: CefrLevel.B2,
    color: '#4C70E8',
  },
  {
    title: 'C1',
    value: CefrLevel.C1,
    color: '#4FBAAD',
  },
  {
    title: 'C2',
    value: CefrLevel.C2,
    color: '#AD2CCC',
  },
];

export const handlerSendToSlack = ({
  errorMessage,
  currentUserAttemptId,
  partId,
  userId,
  phone,
  email,
}: {
  errorMessage: string;
  currentUserAttemptId: string;
  partId?: string;
  userId?: string;
  phone?: string | null;
  email?: string | null;
}) => {
  // void fetch('https://hooks.slack.com/services/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', {
  //   method: 'POST',
  //   body: JSON.stringify({
  //     text: `message:${errorMessage}----userAttemptId:${currentUserAttemptId}----partId:${
  //       partId || ''
  //     }---userId:${userId || ''}---phone:${phone || ''}---email:${email || ''}`,
  //   }),
  // });
};
export const zaloLink = 'https://zalo.me/flyerschool';
export const WhatAppLink = 'https://api.whatsapp.com/send?phone=8485151197';

export const convertStyleStrToObject = (styleString: string) => {
  if (!styleString) return {};

  return styleString.split(';').reduce((styleObject, rule) => {
    const [property, value] = rule.split(':');
    if (!property || !value) return styleObject;
    return { ...styleObject, [property.trim()]: value.trim() };
  }, {});
};

/**
 * Đọc width/height của thẻ <img> trong HTML do CKEditor sinh ra, ra số px cho next/image.
 *
 * `toNumber` của lodash trả NaN cho "300px", "100%", "auto" - mà next/image bắt buộc
 * width/height là số px. Hậu quả của NaN:
 *   - dev: throw 'Image with src ... has invalid "width" property ... received "NaN"'
 *   - prod: URL ảnh ra ...&w=NaN -> server trả 400
 *           '"w" parameter (width) must be an integer greater than 0' -> ảnh chết trắng
 *
 * Thứ tự ưu tiên: attribute -> giá trị tương ứng trong style -> fallback. Giá trị '%'
 * bị bỏ qua vì không quy ra px được; kích thước hiển thị thật vẫn do style quyết định,
 * width/height ở đây chỉ dùng cho tỉ lệ khung và cho việc chọn ảnh trong srcset.
 */
export const parseImagePxSize = (attrValue?: string, styleValue?: string, fallback = 300) => {
  for (const raw of [attrValue, styleValue]) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed || trimmed.endsWith('%')) continue;
    const parsed = Number.parseFloat(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed);
  }
  return fallback;
};

export const SESSION_STORAGE_KEYS = {
  HAS_SHOW_DAILY_GIFT_MODAL: 'HAS_SHOW_DAILY_GIFT_MODAL',
};

export const convertTimeToMinSecond = (second: number) => {
  const minutes = Math.floor(second / 60);
  const seconds = Math.floor(second % 60);
  return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
};

export enum IeltsHomeTabs {
  Exam = 'exam',
  Practice = 'practice',
}

export const IELTS_BAND = [
  {
    title: 'Band 4-5',
    value: '4-5',
    min: 4,
    max: 5,
  },
  {
    title: 'Band 5-6.5',
    value: '5-6.5',
    min: 5,
    max: 6.5,
  },
  {
    title: 'Band 6.5-7.5',
    value: '6.5-7.5',
    min: 6.5,
    max: 7.5,
  },
];

/**
 * The bands the library filter offers: the three target bands plus the two open
 * ends, which only make sense while browsing and so are not offered as a target
 * on the home screen. `value` stays a `min-max` string because the
 * `ieltsBandScore` param is shared with the other list screens, which parse it
 * that way. `range` is what the library filters on, so the open ends can leave
 * their boundary to the neighbouring band instead of overlapping it.
 */
export const IELTS_LIBRARY_BANDS: {
  title: string;
  value: string;
  range: FloatRange;
}[] = [
  { title: 'Band < 4.0', value: '0-4', range: { gte: 0, lt: 4 } },
  ...IELTS_BAND.map(({ title, value, min, max }) => ({
    title,
    value,
    range: { gte: min, lte: max },
  })),
  { title: 'Band > 7.5', value: '7.5-9', range: { gt: 7.5, lte: 9 } },
];

/**
 * Quiz check points are tagged with CEFR levels while study material carries
 * IELTS bands, so the band filter has to be projected onto CEFR before it can
 * narrow the quiz list. Keyed by `IELTS_LIBRARY_BANDS[].value` so there is one
 * source of truth for the band itself. The five bands partition the CEFR
 * ladder: everything at A2 or below sits under band 4.0, and C2 above 7.5.
 */
export const IELTS_BAND_CEFR_LEVELS: Record<string, CefrLevel[]> = {
  '0-4': [CefrLevel.Prea1, CefrLevel.A1, CefrLevel.A2],
  '4-5': [CefrLevel.B1],
  '5-6.5': [CefrLevel.B2],
  '6.5-7.5': [CefrLevel.C1],
  '7.5-9': [CefrLevel.C2],
};

export const convertCountdown = (second: number) => {
  const hour = Math.floor(second / 3600);
  const minute = Math.floor((second % 3600) / 60);
  const sec = second % 60;
  return hour === 0
    ? `${minute < 10 ? `0${minute}` : minute}:${sec < 10 ? `0${sec}` : sec}`
    : `${hour}:${minute < 10 ? `0${minute}` : minute}:${sec < 10 ? `0${sec}` : sec}`;
};

export const formatNumberToK = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(num % 1000 === 0 ? 0 : 1) + 'k';
  }
  return num.toString();
};

export const openModalReportBugAtom = atom<boolean>(false);

export interface UploadedFile {
  id: string;
  blurHash?: string;
  url?: string;
  fileName?: string;
}
/**
 * Upload file (image or media) for bug reports and other purposes
 * @param file - File or Blob to upload
 * @param options - Upload options (type: 'image' | 'media', transparent: boolean)
 * @returns Object with url and id of uploaded file
 */
export const uploadFile = async (
  file: File,
  options: {
    type?: 'image' | 'audio' | 'avatar' | 'media' | 'video';
    transparent?: boolean;
    onProgress?: (progress: number) => void;
  } = {},
): Promise<UploadedFile> => {
  const { type = 'image', transparent = false, onProgress } = options;

  if (!process.env.NEXT_PUBLIC_APP_API) {
    throw new Error('NEXT_PUBLIC_APP_API is not defined');
  }

  const formData = new FormData();
  formData.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      });
    }
    xhr.addEventListener('load', async () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          const uploadedFile = response.data as UploadedFile;

          // Generate URL if not provided by server
          if (!uploadedFile.url && uploadedFile.id) {
            const mediaType = type === 'media' || type === 'video' ? 'audio' : type;
            uploadedFile.url = getMediaUrl(uploadedFile.id, mediaType);
          }

          resolve(uploadedFile);
        } catch (error) {
          reject(new Error('Failed to parse response'));
        }
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });
    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });
    xhr.open('POST', `/app-api/${type}${transparent ? '?transparent=true' : ''}`);
    xhr.send(formData);
  });
};

/**
 * Randomly shuffles an array using the Fisher-Yates algorithm
 * @param array The array to shuffle
 * @returns A new shuffled array
 */
export function shuffleArray<T>(array: T[]): T[] {
  // Create a copy to avoid modifying the original array
  const shuffled = [...array];

  // Fisher-Yates shuffle algorithm
  for (let i = shuffled.length - 1; i > 0; i--) {
    // Pick a random index from 0 to i
    const j = Math.floor(Math.random() * (i + 1));
    // Swap elements at positions i and j
    [shuffled[i], shuffled[j]] = [shuffled[j]!, shuffled[i]!];
  }

  return shuffled;
}
